import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './TeamManagement.css';
import { getOrgPermissions } from '../../utils/permissions';
import i18n from '../../i18n';

const ORG_ROLES = ['ORG_ADMIN', 'MEMBER', 'AUDITOR', 'VIEWER', 'CISO_RESELLER', 'CPA_AUDITOR', 'CPA_ADMIN'];
const ROLE_LABELS = {
  ORG_ADMIN: 'roleOrgAdmin', MEMBER: 'roleMember', AUDITOR: 'roleAuditor', VIEWER: 'roleViewer',
  CISO_RESELLER: 'roleCisoReseller', CPA_AUDITOR: 'roleCpaAuditor', CPA_ADMIN: 'roleCpaAdmin',
};
const ROLE_COLORS = {
  ORG_ADMIN:     { bg: '#dbeafe', text: '#1e40af' },
  MEMBER:        { bg: '#d1fae5', text: '#065f46' },
  AUDITOR:       { bg: '#ede9fe', text: '#5b21b6' },
  VIEWER:        { bg: '#f1f5f9', text: '#475569' },
  CISO_RESELLER: { bg: '#fef3c7', text: '#92400e' },
  CPA_AUDITOR:   { bg: '#ecfdf5', text: '#047857' },
  CPA_ADMIN:     { bg: '#f0fdf4', text: '#166534' },
};
const SOURCE_COLORS = {
  OKTA:    { bg: '#fef3c7', text: '#92400e', icon: 'fa-id-badge' },
  AWS_IAM: { bg: '#fde8d8', text: '#9a3412', icon: 'fa-cloud' },
  O365:    { bg: '#dbeafe', text: '#1e40af', icon: 'fa-microsoft' },
  GITHUB:  { bg: '#f1f5f9', text: '#1e293b', icon: 'fa-github' },
  LDAP:    { bg: '#d1fae5', text: '#065f46', icon: 'fa-server' },
};

function RoleBadge({ role }) {
  const c = ROLE_COLORS[role] || ROLE_COLORS.MEMBER;
  return (
    <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
      background: c.bg, color: c.text }}>
      {ROLE_LABELS[role] ? i18n.t(`TeamManagement.${ROLE_LABELS[role]}`) : role}
    </span>
  );
}

function Avatar({ name, size = 36 }) {
  const initials = (name || '?').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: '#1e3a5f',
      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size * 0.38, flexShrink: 0 }}>
      {initials}
    </div>
  );
}

// Error responses come back as an ErrorResponseDTO OBJECT ({ message, errorKey, entityName }).
// Passing that object to setMsg() and then rendering {msg} in JSX crashes React
// ("Objects are not valid as a React child") — the white-screen bug on re-adding a user.
// Always coerce to a readable string.
function errText(err, fallback) {
  const d = err?.response?.data;
  if (typeof d === 'string') return d;
  return d?.message || d?.error || fallback;
}

export default function TeamManagement() {
  const { t } = useTranslation();
  const [members, setMembers]               = useState([]);
  const [discovered, setDiscovered]         = useState([]);
  const [resettingId, setResettingId]       = useState(null);
  const [mfaResettingId, setMfaResettingId] = useState(null);
  const [resetResult, setResetResult]       = useState(null); // { name, tempPassword }
  const [loading, setLoading]               = useState(true);
  const [tab, setTab]                       = useState('members');
  const [showInvite, setShowInvite]         = useState(false);
  const [inviteForm, setInviteForm]         = useState({ firstName: '', lastName: '', email: '', orgRole: 'MEMBER', message: '' });
  const [inviteResult, setInviteResult]     = useState(null);
  const [inviting, setInviting]             = useState(false);
  const [msg, setMsg]                       = useState('');
  const [showImport, setShowImport]         = useState(null); // discovered identity being imported
  const [importForm, setImportForm]         = useState({ userName: '', password: '', orgRole: 'MEMBER' });

  const currentUser = localStorage.getItem('username');

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      axiosInstance.get('/org-team/members'),
      axiosInstance.get('/org-team/discovered'),
    ]).then(([mRes, dRes]) => {
      if (mRes.status === 'fulfilled') setMembers(mRes.value.data);
      if (dRes.status === 'fulfilled') setDiscovered(dRes.value.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.firstName) { setMsg(t('TeamManagement.errFirstNameEmailRequired')); return; }
    setInviting(true); setMsg('');
    try {
      const res = await axiosInstance.post('/org-team/invite', inviteForm);
      setInviteResult(res.data);
      fetchData();
    } catch (err) {
      setMsg(errText(err, t('TeamManagement.errSendInvitation')));
    } finally { setInviting(false); }
  };

  const handleRoleChange = async (userId, orgRole) => {
    try {
      await axiosInstance.put(`/org-team/members/${userId}/role`, { orgRole });
      fetchData();
    } catch { setMsg(t('TeamManagement.errUpdateRole')); }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm(t('TeamManagement.confirmRemoveMember'))) return;
    try {
      await axiosInstance.delete(`/org-team/members/${userId}`);
      fetchData();
    } catch { setMsg(t('TeamManagement.errRemoveMember')); }
  };

  const handleResetPassword = async (m) => {
    if (!window.confirm(t('TeamManagement.confirmResetPassword', { name: `${m.firstName} ${m.lastName}` }))) return;
    setResettingId(m.id);
    try {
      const res = await axiosInstance.post(`/users/${m.id}/reset-password`);
      setResetResult({ name: `${m.firstName} ${m.lastName}`, tempPassword: res.data.tempPassword });
    } catch (e) {
      setMsg(e.response?.data?.error || t('TeamManagement.errResetPassword'));
    } finally {
      setResettingId(null);
    }
  };

  const handleResetMfa = async (m) => {
    if (!window.confirm(t('TeamManagement.confirmResetMfa', { name: `${m.firstName} ${m.lastName}` }))) return;
    setMfaResettingId(m.id);
    try {
      await axiosInstance.post(`/users/${m.id}/reset-mfa`);
      setMsg(t('TeamManagement.toastMfaReset', { name: `${m.firstName} ${m.lastName}` }));
    } catch (e) {
      setMsg(e.response?.data?.error || t('TeamManagement.errResetMfa'));
    } finally {
      setMfaResettingId(null);
    }
  };

  const handleMap = async (identityId, userId) => {
    try {
      await axiosInstance.post(`/org-team/discovered/${identityId}/map`, { userId });
      fetchData();
      setMsg(t('TeamManagement.toastIdentityMapped'));
    } catch { setMsg(t('TeamManagement.errMapIdentity')); }
  };

  const handleIgnore = async (identityId) => {
    try {
      await axiosInstance.post(`/org-team/discovered/${identityId}/ignore`);
      fetchData();
    } catch { setMsg(t('TeamManagement.errIgnoreIdentity')); }
  };

  const handleImport = async () => {
    if (!importForm.userName || !importForm.password) { setMsg(t('TeamManagement.errUsernamePasswordRequired')); return; }
    try {
      await axiosInstance.post(`/org-team/discovered/${showImport.id}/import`, importForm);
      setShowImport(null);
      fetchData();
      setMsg(t('TeamManagement.toastUserImported'));
    } catch (err) {
      setMsg(errText(err, t('TeamManagement.errImportFailed')));
    }
  };

  const activeMembers = members.filter(m => !m.pending && m.isActive !== false);
  const pendingInvites = members.filter(m => m.pending);
  const newDiscovered = discovered.filter(d => d.status === 'NEW');
  const processedDiscovered = discovered.filter(d => d.status !== 'NEW');

  const { canManageUsers } = getOrgPermissions();

  return (
    <div className="tm-wrap">
      <div className="tm-header-row">
        <div>
          <h2 className="tm-title">{t('TeamManagement.title')}</h2>
          <p className="tm-subtitle">{activeMembers.length} {activeMembers.length !== 1 ? t('TeamManagement.membersPlural') : t('TeamManagement.memberSingular')} · {pendingInvites.length} {t('TeamManagement.pendingCount')} · {newDiscovered.length} {t('TeamManagement.discoveredCount')}</p>
        </div>
        {canManageUsers && (
          <button className="tm-invite-btn" onClick={() => { setShowInvite(true); setInviteResult(null); setMsg(''); }}>
            <i className="fas fa-user-plus" /> {t('TeamManagement.inviteMember')}
          </button>
        )}
      </div>

      {msg && <div className="tm-msg">{msg}</div>}

      <div className="tm-tabs">
        <button className={`tm-tab ${tab === 'members' ? 'active' : ''}`} onClick={() => setTab('members')}>
          <i className="fas fa-users" /> {t('TeamManagement.tabMembers')} {activeMembers.length > 0 && <span className="tm-count">{activeMembers.length}</span>}
        </button>
        <button className={`tm-tab ${tab === 'pending' ? 'active' : ''}`} onClick={() => setTab('pending')}>
          <i className="fas fa-clock" /> {t('TeamManagement.tabPending')} {pendingInvites.length > 0 && <span className="tm-count">{pendingInvites.length}</span>}
        </button>
        <button className={`tm-tab ${tab === 'discovered' ? 'active' : ''}`} onClick={() => setTab('discovered')}>
          <i className="fas fa-radar" /> {t('TeamManagement.tabDiscovered')} {newDiscovered.length > 0 && <span className="tm-count tm-count-warn">{newDiscovered.length}</span>}
        </button>
      </div>

      {loading ? <div className="tm-loading"><i className="fas fa-spinner fa-spin" /> {t('TeamManagement.loading')}</div> : (
        <>
          {/* Members tab */}
          {tab === 'members' && (
            <div className="tm-list">
              {activeMembers.length === 0 && <div className="tm-empty">{t('TeamManagement.emptyActiveMembers')}</div>}
              {activeMembers.map(m => (
                <div key={m.id} className="tm-member-row">
                  <Avatar name={`${m.firstName || ''} ${m.lastName || ''}`} />
                  <div className="tm-member-info">
                    <div className="tm-member-name">{m.firstName} {m.lastName}
                      {m.userName === currentUser && <span className="tm-you">{t('TeamManagement.you')}</span>}
                    </div>
                    <div className="tm-member-email">{m.email}</div>
                  </div>
                  <div className="tm-member-role">
                    <RoleBadge role={m.orgRole} />
                  </div>
                  {m.userName !== currentUser && canManageUsers && (
                    <div className="tm-member-actions">
                      <select className="tm-role-select" value={m.orgRole}
                        onChange={e => handleRoleChange(m.id, e.target.value)}>
                        {ORG_ROLES.map(r => <option key={r} value={r}>{t(`TeamManagement.${ROLE_LABELS[r]}`)}</option>)}
                      </select>
                      <button className="tm-remove-btn" title={t('TeamManagement.resetPasswordTitle')} disabled={resettingId === m.id}
                        onClick={() => handleResetPassword(m)}>
                        <i className="fas fa-key" />
                      </button>
                      <button className="tm-remove-btn" title={t('TeamManagement.resetMfaTitle')} disabled={mfaResettingId === m.id}
                        onClick={() => handleResetMfa(m)}>
                        <i className="fas fa-mobile-alt" />
                      </button>
                      <button className="tm-remove-btn" title={t('TeamManagement.removeMemberTitle')} onClick={() => handleRemove(m.id)}>
                        <i className="fas fa-user-minus" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pending invitations tab */}
          {tab === 'pending' && (
            <div className="tm-list">
              {pendingInvites.length === 0 && <div className="tm-empty">{t('TeamManagement.emptyPending')}</div>}
              {pendingInvites.map(inv => (
                <div key={inv.id} className="tm-member-row">
                  <div className="tm-pending-avatar"><i className="fas fa-envelope" /></div>
                  <div className="tm-member-info">
                    <div className="tm-member-name">{inv.firstName} {inv.lastName}</div>
                    <div className="tm-member-email">{inv.email}</div>
                    {inv.expiresAt && (
                      <div className="tm-expires">{t('TeamManagement.expires', { date: new Date(inv.expiresAt).toLocaleDateString() })}</div>
                    )}
                  </div>
                  <RoleBadge role={inv.orgRole} />
                  <div className="tm-member-actions">
                    <button className="tm-copy-btn" title={t('TeamManagement.copyInviteLinkTitle')}
                      onClick={() => { navigator.clipboard.writeText(window.location.origin + '/' + inv.inviteLink); setMsg(t('TeamManagement.toastInviteLinkCopied')); }}>
                      <i className="fas fa-copy" /> {t('TeamManagement.copyLink')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Discovered identities tab */}
          {tab === 'discovered' && (
            <div>
              <p className="tm-discovered-info">
                <i className="fas fa-info-circle" /> {t('TeamManagement.discoveredInfo')}
              </p>
              {newDiscovered.length === 0 && processedDiscovered.length === 0 && (
                <div className="tm-empty">{t('TeamManagement.emptyDiscovered')}</div>
              )}
              {newDiscovered.length > 0 && (
                <>
                  <h4 className="tm-section-label">{t('TeamManagement.sectionNewNeedsReview')}</h4>
                  {newDiscovered.map(di => {
                    const src = SOURCE_COLORS[di.source] || SOURCE_COLORS.LDAP;
                    return (
                      <div key={di.id} className="tm-discovered-row">
                        <div className="tm-source-badge" style={{ background: src.bg, color: src.text }}>
                          <i className={`fab ${src.icon}`} /> {di.source.replace('_', ' ')}
                        </div>
                        <div className="tm-member-info">
                          <div className="tm-member-name">{di.displayName || di.email}</div>
                          <div className="tm-member-email">{di.email}</div>
                          {di.externalRole && <div className="tm-ext-role"><i className="fas fa-tag" /> {di.externalRole}</div>}
                        </div>
                        {di.suggestedMatchUserId && (
                          <span className="tm-match-hint">
                            <i className="fas fa-link" /> {t('TeamManagement.matchFound')}
                          </span>
                        )}
                        <div className="tm-discovered-actions">
                          {di.suggestedMatchUserId && (
                            <button className="tm-map-btn" onClick={() => handleMap(di.id, di.suggestedMatchUserId)}>
                              <i className="fas fa-link" /> {t('TeamManagement.mapToExisting')}
                            </button>
                          )}
                          <button className="tm-import-btn" onClick={() => {
                            setShowImport(di);
                            const suggested = (di.displayName || di.email || '').toLowerCase().replace(/[^a-z0-9.]/g, '.').replace(/\.+/g, '.');
                            setImportForm({ userName: suggested, password: '', orgRole: 'MEMBER' });
                          }}>
                            <i className="fas fa-user-plus" /> {t('TeamManagement.import')}
                          </button>
                          <button className="tm-ignore-btn" onClick={() => handleIgnore(di.id)}>
                            <i className="fas fa-times" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
              {processedDiscovered.length > 0 && (
                <>
                  <h4 className="tm-section-label" style={{ marginTop: 24 }}>{t('TeamManagement.sectionProcessed')}</h4>
                  {processedDiscovered.map(di => {
                    const src = SOURCE_COLORS[di.source] || SOURCE_COLORS.LDAP;
                    return (
                      <div key={di.id} className="tm-discovered-row tm-processed">
                        <div className="tm-source-badge" style={{ background: src.bg, color: src.text }}>
                          <i className={`fab ${src.icon}`} /> {di.source.replace('_', ' ')}
                        </div>
                        <div className="tm-member-info">
                          <div className="tm-member-name">{di.displayName || di.email}</div>
                          <div className="tm-member-email">{di.email}</div>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: di.status === 'MAPPED' ? '#065f46' : '#1e40af',
                          background: di.status === 'MAPPED' ? '#d1fae5' : '#dbeafe',
                          padding: '2px 10px', borderRadius: 12 }}>{di.status}</span>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div className="tm-overlay" onClick={() => setShowInvite(false)}>
          <div className="tm-modal" onClick={e => e.stopPropagation()}>
            <div className="tm-modal-header">
              <h3>{t('TeamManagement.inviteTeamMember')}</h3>
              <button onClick={() => setShowInvite(false)}><i className="fas fa-times" /></button>
            </div>
            {inviteResult ? (
              <div className="tm-invite-success">
                <i className="fas fa-check-circle" style={{ color: '#10b981', fontSize: 32, marginBottom: 12 }} />
                {inviteResult.reactivated ? (
                  <p>{t('TeamManagement.memberReactivated', { email: inviteResult.email })}</p>
                ) : (
                  <>
                    <p>{t('TeamManagement.invitationCreatedPrefix')} <strong>{inviteForm.email}</strong>{t('TeamManagement.invitationCreatedSuffix')}</p>
                    <div className="tm-invite-link">
                      {window.location.origin}/#/accept-invite?token={inviteResult.token}
                    </div>
                    <button className="tm-copy-link-btn" onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/#/accept-invite?token=${inviteResult.token}`);
                      setMsg(t('TeamManagement.toastLinkCopied'));
                    }}>
                      <i className="fas fa-copy" /> {t('TeamManagement.copyLink')}
                    </button>
                    <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 8 }}>
                      {t('TeamManagement.expiresLabel', { date: new Date(inviteResult.expiresAt).toLocaleDateString() })}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="tm-modal-body">
                  {[[t('TeamManagement.labelFirstName'), t('TeamManagement.placeholderFirstName'), 'text', 'firstName'],
                    [t('TeamManagement.labelLastName'), t('TeamManagement.placeholderLastName'), 'text', 'lastName'],
                    [t('TeamManagement.labelEmail'), t('TeamManagement.placeholderEmail'), 'email', 'email']].map(([label, placeholder, type, key]) => (
                    <div key={key} className="tm-field">
                      <label>{label}</label>
                      <input type={type} value={inviteForm[key]}
                        onChange={e => setInviteForm(f => ({ ...f, [key]: e.target.value }))}
                        placeholder={placeholder} />
                    </div>
                  ))}
                  <div className="tm-field">
                    <label>{t('TeamManagement.labelRole')}</label>
                    <select value={inviteForm.orgRole}
                      onChange={e => setInviteForm(f => ({ ...f, orgRole: e.target.value }))}>
                      {ORG_ROLES.map(r => <option key={r} value={r}>{t(`TeamManagement.${ROLE_LABELS[r]}`)}</option>)}
                    </select>
                  </div>
                  <div className="tm-field">
                    <label>{t('TeamManagement.labelPersonalMessage')}</label>
                    <textarea rows={2} value={inviteForm.message}
                      onChange={e => setInviteForm(f => ({ ...f, message: e.target.value }))}
                      placeholder={t('TeamManagement.placeholderPersonalMessage')} />
                  </div>
                  {msg && <div className="tm-error">{msg}</div>}
                </div>
                <div className="tm-modal-footer">
                  <button className="tm-btn-secondary" onClick={() => setShowInvite(false)}>{t('TeamManagement.cancel')}</button>
                  <button className="tm-btn-primary" onClick={handleInvite} disabled={inviting}>
                    {inviting ? t('TeamManagement.sending') : <><i className="fas fa-paper-plane" /> {t('TeamManagement.sendInvitation')}</>}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImport && (
        <div className="tm-overlay" onClick={() => setShowImport(null)}>
          <div className="tm-modal" onClick={e => e.stopPropagation()}>
            <div className="tm-modal-header">
              <h3>{t('TeamManagement.importAsPlatformUser')}</h3>
              <button onClick={() => setShowImport(null)}><i className="fas fa-times" /></button>
            </div>
            <div className="tm-modal-body">
              <p style={{ color: '#64748b', marginBottom: 16 }}>
                {t('TeamManagement.importingPrefix')} <strong>{showImport.displayName || showImport.email}</strong> {t('TeamManagement.importingFrom', { source: showImport.source })}
              </p>
              {[[t('TeamManagement.labelUsername'), t('TeamManagement.placeholderUsername'), 'text', 'userName'],
                [t('TeamManagement.labelInitialPassword'), t('TeamManagement.placeholderInitialPassword'), 'password', 'password']].map(([label, placeholder, type, key]) => (
                <div key={key} className="tm-field">
                  <label>{label}</label>
                  <input type={type} value={importForm[key]}
                    onChange={e => setImportForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder} />
                </div>
              ))}
              <div className="tm-field">
                <label>{t('TeamManagement.labelRole')}</label>
                <select value={importForm.orgRole}
                  onChange={e => setImportForm(f => ({ ...f, orgRole: e.target.value }))}>
                  {ORG_ROLES.map(r => <option key={r} value={r}>{t(`TeamManagement.${ROLE_LABELS[r]}`)}</option>)}
                </select>
              </div>
              {msg && <div className="tm-error">{msg}</div>}
            </div>
            <div className="tm-modal-footer">
              <button className="tm-btn-secondary" onClick={() => setShowImport(null)}>{t('TeamManagement.cancel')}</button>
              <button className="tm-btn-primary" onClick={handleImport}>
                <i className="fas fa-user-plus" /> {t('TeamManagement.importUser')}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetResult && (
        <div className="tm-overlay" onClick={() => setResetResult(null)}>
          <div className="tm-modal" onClick={e => e.stopPropagation()}>
            <div className="tm-modal-header"><h3>{t('TeamManagement.tempPasswordTitle')}</h3></div>
            <div className="tm-modal-body">
              <p style={{ fontSize: '0.875rem', color: '#475569' }}>
                {t('TeamManagement.tempPasswordPrefix')} <strong>{resetResult.name}</strong> {t('TeamManagement.tempPasswordSuffix')}
              </p>
              <code style={{ display: 'block', padding: '10px 12px', background: '#f1f5f9',
                             borderRadius: 6, fontSize: '1rem', letterSpacing: 1, userSelect: 'all' }}>
                {resetResult.tempPassword}
              </code>
            </div>
            <div className="tm-modal-footer">
              <button className="tm-btn-secondary"
                onClick={() => navigator.clipboard.writeText(resetResult.tempPassword)}>
                <i className="fas fa-copy" /> {t('TeamManagement.copy')}
              </button>
              <button className="tm-btn-primary" onClick={() => setResetResult(null)}>{t('TeamManagement.done')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
